import { useParams, Link } from 'react-router-dom';
import { categories, getComicsByCategory, comics } from '@/mocks/comics';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHelmet from '@/components/base/SEOHelmet';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const currentCat = categories.find((c) => c.slug === slug) || categories[0];
  const categoryComics = getComicsByCategory(slug || 'korean');
  const allCategoryComics = slug
    ? categoryComics
    : comics;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCat.name}专区 - 55漫画网`,
    description: `55漫画网${currentCat.name}专区，免费在线阅读全部${currentCat.name}作品，高清画质，极速加载。`,
    url: `http://junyitea.cn/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '55漫画网',
      url: 'http://junyitea.cn/',
    },
  };

  return (
    <>
      <SEOHelmet
        title={`${currentCat.name}专区 - 免费${currentCat.name}在线阅读 - 55漫画网`}
        description={`55漫画网${currentCat.name}专区提供${currentCat.name}全集免费在线阅读，海量${currentCat.name}资源，高清画质，每日更新。`}
        keywords={`${currentCat.name}, ${currentCat.name}免费阅读, ${currentCat.name}全集, 55漫画网, 漫画在线`}
        canonicalUrl={`http://junyitea.cn/category/${slug}`}
        jsonLd={jsonLd}
      />

      <Navbar />

      <main>
        <section className="pt-24 pb-10 md:pt-28 md:pb-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: currentCat.name },
              ]}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center mt-4">
              <div className="relative rounded-lg overflow-hidden bg-secondary-100 h-64 md:h-80">
                <img
                  src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20art%20style%20illustration%20featuring%20comic%20characters%20in%20a%20dynamic%20composition%2C%20warm%20color%20palette%2C%20artistic%20digital%20illustration%2C%20atmospheric%20lighting%2C%20clean%20modern%20aesthetic%2C%20soft%20background&width=800&height=500&seq=cat-bg-default&orientation=landscape"
                  alt={currentCat.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent flex items-center">
                  <div className="p-8">
                    <span className="text-xs text-background-50/80 mb-2 block">55漫画网</span>
                    <h1 className="text-3xl md:text-4xl font-heading font-bold text-background-50 leading-tight">
                      {currentCat.name}
                      <br />
                      专 区
                    </h1>
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-center md:items-start text-center md:text-left">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                  {currentCat.name}
                  <br />
                  精选作品
                </h2>
                <p className="text-sm text-foreground-500 leading-relaxed mb-6 max-w-md">
                  55漫画网{currentCat.name}专区，为你精选最优质的{currentCat.name}作品。从热门连载到经典完结，从恋爱甜宠到热血奇幻，总有一部适合你。
                </p>
                <Link
                  to={`/comic/${allCategoryComics[0]?.id || 'jiajiao-laoshi'}`}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  立即阅读 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-6 px-4 md:px-6 bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
              {allCategoryComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>

            {allCategoryComics.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                  <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无作品，请浏览其他分类</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}